import React, { useState, useEffect, useCallback } from "react";
import { Tab, Table, Button, Label, Dropdown, Message, Loader, Pagination, Icon, Input, Header } from "semantic-ui-react";
import { Integration, Company, Site } from "../../../api";
import { useAuth } from "../../../hooks";
import { isMaster } from "../../../utils/checkPermission";
import { ErrorAccessDenied } from "../Error";
import "./Integrations.scss";

const integrationController = new Integration();
const companyController = new Company();
const siteController = new Site();

// Pantalla del master: estado de la integración con la otra app Sustemia
// (empresas vinculadas, usuarios con acceso cruzado, sincronizaciones
// pendientes) y auditoría de todo lo que pasó. Ver server/utils/integration.js.

const EVENT_LABELS = {
  products_changed: "Productos cambiados",
  company_linked: "Empresa vinculada",
  sync_failed: "Sincronización con error",
  sync_recovered: "Sincronización recuperada",
  access_granted: "Acceso habilitado",
  access_role_changed: "Rol cambiado",
  access_revoked: "Acceso quitado",
  access_applied: "Cambio de acceso aplicado",
  crossed_out: "Pasó a la otra app",
  sso_login: "Ingresó desde la otra app",
  sso_rejected: "Ingreso rechazado",
  site_deleted: "Sitio borrado",
  company_deleted: "Empresa borrada",
  company_unlinked: "Empresa desvinculada",
  accounts_linked: "Cuentas vinculadas",
  account_unlinked: "Cuenta desvinculada",
};

const EVENT_COLORS = {
  sync_failed: "red",
  sso_rejected: "red",
  access_revoked: "orange",
  site_deleted: "orange",
  company_deleted: "orange",
  company_unlinked: "orange",
  sync_recovered: "green",
  access_granted: "green",
  sso_login: "blue",
};

const REJECT_REASONS = {
  token_invalid: "enlace vencido o inválido",
  token_reused: "enlace ya usado",
  company_not_linked: "empresa no vinculada",
  product_not_contracted: "producto no contratado",
  company_inactive: "empresa inactiva",
  home_account_not_found: "no se encontró la cuenta original",
  home_access_disabled: "acceso deshabilitado en origen",
  user_inactive: "usuario desactivado",
  role_not_found: "rol inexistente",
  mirror_other_company: "cuenta en otra empresa",
  email_taken_by_own_account: "ya existe una cuenta propia con ese email (se puede vincular)",
  link_mismatch: "el vínculo de cuentas no coincide",
};

const LOGIN_MODES = { created: "cuenta creada", mirror: "cuenta espejo", home: "volvió a su cuenta", linked: "cuenta vinculada" };

function formatDate(value) {
  return value ? new Date(value).toLocaleString("es-AR") : "";
}

function describeEvent(event) {
  const d = event.details || {};
  switch (event.type) {
    case "products_changed":
      return Object.entries(d.after || {})
        .map(([product, on]) => `${product}: ${d.before?.[product] ? "sí" : "no"} → ${on ? "sí" : "no"}`)
        .join(", ");
    case "access_granted":
      return `Rol allá: ${d.after?.roleName || "-"}`;
    case "access_role_changed":
      return `${d.before?.roleName || "-"} → ${d.after?.roleName || "-"}`;
    case "access_applied":
      return d.enabled ? `Habilitado como ${d.roleName}` : "Cuenta desactivada";
    case "crossed_out":
      return [d.homeTrip ? "volvió a su cuenta original" : `rol allá: ${d.roleName || "-"}`, d.mfa ? "con 2FA" : "sin 2FA"].join(" · ");
    case "sso_login":
      return [LOGIN_MODES[d.mode] || d.mode, d.outcome === "2fa_required" ? "se pidió código 2FA" : d.outcome === "session" ? "sesión iniciada" : d.outcome].filter(Boolean).join(" · ");
    case "sso_rejected":
      return REJECT_REASONS[d.reason] || d.reason || "";
    case "sync_failed":
      return d.msg || "";
    case "site_deleted":
      return `${d.name || ""} (${d.cuit || "-"}) · en la otra app se conserva`;
    case "company_deleted":
      return [`${d.name || ""} (${d.cuit || "-"})`, d.linked ? (d.peerNotified ? "desvinculada en la otra app" : "no se pudo avisar a la otra app") : null].filter(Boolean).join(" · ");
    case "company_unlinked":
      return "La otra app borró su empresa: se conservan los datos de acá";
    case "accounts_linked":
      return `${d.count || 0} cuenta(s): ${(d.emails || []).join(", ")}`;
    default:
      return "";
  }
}

function YesNo({ value, yes = "Sí", no = "No" }) {
  return <Label size="small" color={value ? "green" : undefined} basic={!value}>{value ? yes : no}</Label>;
}

export function Integrations() {
  const { accessToken, user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null);
  const [notice, setNotice] = useState(null);

  const loadOverview = useCallback(async () => {
    try {
      setError("");
      setOverview(await integrationController.getAdminOverview(accessToken));
    } catch (err) {
      setError(err.msg || "No se pudo cargar el estado de la integración");
    }
  }, [accessToken]);

  useEffect(() => {
    if (isMaster(user?.role)) loadOverview();
  }, [loadOverview, user]);

  if (!isMaster(user?.role)) return <ErrorAccessDenied />;
  if (error) return <Message negative content={error} />;
  if (!overview) return <Loader active inline="centered" />;

  const { app, peer, companies, users, configured } = overview;
  const pendingCompanies = companies.filter((c) => c.syncPending).length;
  const pendingUsers = users.filter((u) => u.syncPending).length;

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    setNotice(null);
    try {
      const result = await fn();
      setNotice({ positive: true, content: typeof okMsg === "function" ? okMsg(result) : okMsg });
    } catch (err) {
      setNotice({ negative: true, content: err.msg || "No se pudo completar la acción" });
    } finally {
      setBusy(null);
      loadOverview();
    }
  };

  const retryAll = () => run("retry", () => integrationController.retryPending(accessToken), (r) =>
    r.stillPending.companies + r.stillPending.users === 0
      ? "Todo sincronizado"
      : `Siguen pendientes: ${r.stillPending.companies} empresa(s), ${r.stillPending.users} usuario(s). ¿${peer.name} está disponible?`);

  const panes = [
    {
      menuItem: `Empresas (${companies.length})`,
      render: () => (
        <Tab.Pane attached={false}>
          <Table compact celled unstackable className="integrations__table">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Empresa</Table.HeaderCell>
                <Table.HeaderCell>CUIT</Table.HeaderCell>
                <Table.HeaderCell>{app.name}</Table.HeaderCell>
                <Table.HeaderCell>{peer.name}</Table.HeaderCell>
                <Table.HeaderCell>Vinculada</Table.HeaderCell>
                <Table.HeaderCell>Sincronización</Table.HeaderCell>
                <Table.HeaderCell />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {companies.map((c) => (
                <Table.Row key={c._id} negative={c.syncPending} disabled={!c.active}>
                  <Table.Cell>{c.name}{!c.active && " (inactiva)"}</Table.Cell>
                  <Table.Cell>{c.cuit}</Table.Cell>
                  <Table.Cell><YesNo value={c.hasApp} /></Table.Cell>
                  <Table.Cell><YesNo value={c.hasPeer} /></Table.Cell>
                  <Table.Cell><YesNo value={c.linked} /></Table.Cell>
                  <Table.Cell>
                    {c.syncPending ? <Label size="small" color="red">Pendiente</Label> : c.hasPeer ? "OK" : "—"}
                  </Table.Cell>
                  <Table.Cell collapsing>
                    {(c.hasPeer || c.syncPending) && configured && (
                      <Button size="mini" basic loading={busy === c._id}
                        onClick={() => run(c._id, () => integrationController.syncCompany(accessToken, c._id), `${c.name}: sincronizada`)}>
                        Sincronizar
                      </Button>
                    )}
                    {c.hasPeer && c.linked && configured && (
                      <Button size="mini" basic loading={busy === `link-${c._id}`}
                        title={`Une las cuentas de ${c.name} que ya existen en las dos apps con el mismo email`}
                        onClick={() => {
                          if (!window.confirm(`¿Vincular las cuentas de ${c.name} que tienen el mismo email en ${app.name} y ${peer.name}? Cada cuenta conserva su rol en cada app.`)) return;
                          run(`link-${c._id}`, () => integrationController.linkAccounts(accessToken, c._id), (r) =>
                            r.linked.length ? `${c.name}: ${r.linked.length} cuenta(s) vinculada(s): ${r.linked.join(", ")}` : `${c.name}: no hay cuentas para vincular`);
                        }}>
                        Vincular cuentas
                      </Button>
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Tab.Pane>
      ),
    },
    {
      menuItem: `Usuarios con acceso cruzado (${users.length})`,
      render: () => (
        <Tab.Pane attached={false}>
          {users.length === 0 ? (
            <Message info content={`Ningún usuario tiene acceso a ${peer.name} todavía. Se habilita desde la ficha de cada usuario.`} />
          ) : (
            <Table compact celled unstackable className="integrations__table">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Usuario</Table.HeaderCell>
                  <Table.HeaderCell>Empresa</Table.HeaderCell>
                  <Table.HeaderCell>Tipo</Table.HeaderCell>
                  <Table.HeaderCell>Rol en {peer.name}</Table.HeaderCell>
                  <Table.HeaderCell>Estado</Table.HeaderCell>
                  <Table.HeaderCell />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {users.map((u) => (
                  <Table.Row key={u._id} negative={u.syncPending}>
                    <Table.Cell>{u.name ? <>{u.name}<br /><small>{u.email}</small></> : u.email}</Table.Cell>
                    <Table.Cell>{u.company || "—"}</Table.Cell>
                    <Table.Cell>
                      {u.kind === "mirror" ? `Creada desde ${peer.name}`
                        : u.kind === "linked" ? `Cuenta vinculada con ${peer.name}`
                        : `Con acceso a ${peer.name}`}
                    </Table.Cell>
                    <Table.Cell>{u.kind === "out" ? u.peerRole || "—" : u.kind === "linked" ? "Su rol de allá" : "—"}</Table.Cell>
                    <Table.Cell>
                      {u.syncPending
                        ? <Label size="small" color="red">Cambio pendiente de aplicar</Label>
                        : u.active ? "Activo" : "Desactivado"}
                    </Table.Cell>
                    <Table.Cell collapsing>
                      {u.kind === "linked" && (
                        <Button size="mini" basic loading={busy === `unlink-${u._id}`}
                          onClick={() => {
                            if (!window.confirm(`¿Desvincular la cuenta de ${u.email}? Ya no va a poder pasar directo entre las apps.`)) return;
                            run(`unlink-${u._id}`, () => integrationController.unlinkAccount(accessToken, u._id), `${u.email}: cuenta desvinculada`);
                          }}>
                          Desvincular
                        </Button>
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </Tab.Pane>
      ),
    },
    {
      menuItem: "Revisión de datos",
      render: () => (
        <Tab.Pane attached={false}>
          <DataReview accessToken={accessToken} configured={configured} onChanged={loadOverview} />
        </Tab.Pane>
      ),
    },
    {
      menuItem: "Auditoría",
      render: () => (
        <Tab.Pane attached={false}>
          <AuditLog accessToken={accessToken} companies={companies} />
        </Tab.Pane>
      ),
    },
  ];

  return (
    <div className="integrations">
      <div className="integrations__header">
        <div>
          <h2>Integración con {peer.name}</h2>
          {configured ? (
            <Label color="green"><Icon name="check" />Configurada</Label>
          ) : (
            <Label color="red"><Icon name="warning" />No configurada</Label>
          )}
          {(pendingCompanies > 0 || pendingUsers > 0) && (
            <Label color="orange">
              Pendientes: {pendingCompanies} empresa(s), {pendingUsers} usuario(s)
            </Label>
          )}
        </div>
        {configured && (pendingCompanies > 0 || pendingUsers > 0) && (
          <Button primary loading={busy === "retry"} onClick={retryAll}>Reintentar pendientes</Button>
        )}
      </div>

      {!configured && (
        <Message warning
          header="La integración está apagada en este servidor"
          content="Faltan INTEGRATION_SECRET (mínimo 32 caracteres, distinto de JWT_SECRET_KEY), PEER_API_URL o PEER_CLIENT_URL en el .env." />
      )}
      {notice && <Message {...notice} onDismiss={() => setNotice(null)} />}

      <Tab menu={{ secondary: true, pointing: true }} panes={panes} />
    </div>
  );
}

function AuditLog({ accessToken, companies }) {
  const [filters, setFilters] = useState({ company: "", type: "", page: 1 });
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setError("");
    integrationController
      .getAdminEvents(accessToken, { ...filters, limit: 50 })
      .then(setData)
      .catch((err) => setError(err.msg || "No se pudo cargar la auditoría"));
  }, [accessToken, filters]);

  const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));

  return (
    <>
      <div className="integrations__filters">
        <Dropdown selection clearable search placeholder="Todas las empresas"
          options={companies.map((c) => ({ key: c._id, value: c._id, text: c.name }))}
          value={filters.company} onChange={(_, d) => setFilter("company", d.value)} />
        <Dropdown selection clearable placeholder="Todos los eventos"
          options={Object.entries(EVENT_LABELS).map(([value, text]) => ({ key: value, value, text }))}
          value={filters.type} onChange={(_, d) => setFilter("type", d.value)} />
      </div>

      {error && <Message negative content={error} />}
      {!data ? <Loader active inline="centered" /> : data.items.length === 0 ? (
        <Message info content="No hay eventos registrados con estos filtros." />
      ) : (
        <>
          <Table compact celled unstackable className="integrations__table">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Fecha</Table.HeaderCell>
                <Table.HeaderCell>Evento</Table.HeaderCell>
                <Table.HeaderCell>Empresa</Table.HeaderCell>
                <Table.HeaderCell>Quién</Table.HeaderCell>
                <Table.HeaderCell>Usuario afectado</Table.HeaderCell>
                <Table.HeaderCell>Detalle</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {data.items.map((e) => (
                <Table.Row key={e._id}>
                  <Table.Cell collapsing>{formatDate(e.createdAt)}</Table.Cell>
                  <Table.Cell collapsing>
                    <Label size="small" color={EVENT_COLORS[e.type]}>{EVENT_LABELS[e.type] || e.type}</Label>
                  </Table.Cell>
                  <Table.Cell>{e.company?.name || "—"}</Table.Cell>
                  <Table.Cell>{e.actorEmail || (e.type === "access_applied" || e.type === "company_linked" ? "Otra app" : "Sistema")}</Table.Cell>
                  <Table.Cell>{e.targetEmail || "—"}</Table.Cell>
                  <Table.Cell>
                    {describeEvent(e)}
                    {e.ip && <div className="integrations__ip">IP {e.ip}</div>}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
          {data.total > data.limit && (
            <Pagination
              activePage={data.page}
              totalPages={Math.ceil(data.total / data.limit)}
              onPageChange={(_, d) => setFilters((prev) => ({ ...prev, page: d.activePage }))}
            />
          )}
        </>
      )}
    </>
  );
}


// ── Revisión de datos ────────────────────────────────────────────────────
// Compara empresas, sitios y usuarios de esta app con los de la otra antes de
// habilitar la integración, y deja corregir acá mismo el CUIT de los datos de
// esta app. Empresas y sitios se reconocen por CUIT, nunca por nombre.

// Campo para corregir el CUIT de una empresa o sitio de esta app.
function CuitEditor({ value, onSave }) {
  const [cuit, setCuit] = useState(value || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await onSave(cuit.trim()); } finally { setSaving(false); }
  };
  return (
    <Input size="mini" value={cuit} onChange={(_, d) => setCuit(d.value)}
      action={<Button size="mini" primary loading={saving} disabled={!cuit.trim() || cuit.trim() === value} onClick={save}>Guardar</Button>} />
  );
}

function ReviewSection({ title, color, help, count, children }) {
  if (!count) return null;
  return (
    <div className="integrations__review-section">
      <Header as="h4">
        <Label color={color} circular>{count}</Label> {title}
      </Header>
      {help && <p className="integrations__review-help">{help}</p>}
      {children}
    </div>
  );
}

function DataReview({ accessToken, configured, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(null);

  const analyze = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await integrationController.getComparison(accessToken));
    } catch (err) {
      setError(err.msg || "No se pudo hacer la revisión");
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  if (!configured) return <Message warning content="La integración no está configurada en este servidor." />;
  if (!data) {
    return (
      <>
        <p>Compara las empresas, sitios y usuarios de las dos apps antes de habilitar la integración, para evitar duplicados y vínculos equivocados. Solo lee: no cambia nada hasta que guardes una corrección.</p>
        {error && <Message negative content={error} />}
        <Button primary loading={loading} onClick={analyze}>Analizar</Button>
      </>
    );
  }

  const { apps, report: r } = data;
  const local = apps.local, peer = apps.peer;
  const saveCompany = (id) => async (cuit) => {
    try {
      await companyController.updateCompany(accessToken, id, { cuit });
      setNotice({ positive: true, content: "CUIT de la empresa actualizado" });
      onChanged?.();
      await analyze();
    } catch (err) {
      setNotice({ negative: true, content: err.msg || "No se pudo guardar (¿ese CUIT ya lo tiene otra empresa?)" });
    }
  };
  const saveSite = (id) => async (cuit) => {
    try {
      await siteController.updateSite(accessToken, id, { cuit });
      setNotice({ positive: true, content: "CUIT del sitio actualizado" });
      await analyze();
    } catch (err) {
      setNotice({ negative: true, content: err.msg || "No se pudo guardar (¿ese CUIT ya lo tiene otro sitio?)" });
    }
  };
  // Celda de un dato: editable si es de esta app, de solo lectura si es de la otra.
  const cuitCell = (item, side, save) => (side === "local"
    ? <CuitEditor key={`${item._id}-${item.cuit}`} value={item.cuit} onSave={save(item._id)} />
    : <span>{item.cuit || "(vacío)"} <small className="integrations__review-help">— corregir en {peer}</small></span>);

  const suspicious = r.companies.matched.filter((m) => m.suspicious);
  const okMatches = r.companies.matched.filter((m) => !m.suspicious);
  const toFix = suspicious.length + r.companies.badCuit.length + r.companies.duplicatedCuit.length + r.sites.badCuit.length + r.sites.conflicts.length;

  return (
    <div className="integrations__review">
      <div className="integrations__review-top">
        {toFix === 0
          ? <Message positive compact content="No hay nada para corregir. Revisá igual los casos marcados para revisar, si los hay." />
          : <Message warning compact content={`Hay ${toFix} caso(s) para corregir antes de habilitar la integración en esas empresas.`} />}
        <Button basic loading={loading} onClick={analyze}><Icon name="refresh" />Volver a analizar</Button>
      </div>
      {notice && <Message {...notice} onDismiss={() => setNotice(null)} />}

      <Header as="h3">Empresas</Header>
      <ReviewSection title="Coincidencia falsa probable — NO habilitar hasta corregir" color="red" count={suspicious.length}
        help="Coinciden por un CUIT de relleno: la integración las trataría como la misma empresa. Cargá el CUIT real.">
        <Table compact celled unstackable>
          <Table.Body>
            {suspicious.map((m) => (
              <Table.Row key={m.local._id}>
                <Table.Cell><b>{local}:</b> {m.local.name}</Table.Cell>
                <Table.Cell>{cuitCell(m.local, "local", saveCompany)}</Table.Cell>
                <Table.Cell><b>{peer}:</b> {m.peer.name}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </ReviewSection>
      <ReviewSection title="CUIT de relleno o mal cargado" color="red" count={r.companies.badCuit.length}
        help="Un identificador extranjero (RTN, RUC…) está bien; estos parecen de relleno o tienen caracteres de más.">
        <Table compact celled unstackable>
          <Table.Body>
            {r.companies.badCuit.map((c) => (
              <Table.Row key={`${c.side}-${c._id}`}>
                <Table.Cell><b>{c.side === "local" ? local : peer}:</b> {c.name}</Table.Cell>
                <Table.Cell>{c.problem}</Table.Cell>
                <Table.Cell>{cuitCell(c, c.side, saveCompany)}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </ReviewSection>
      <ReviewSection title="CUIT repetido dentro de una misma app" color="red" count={r.companies.duplicatedCuit.length}
        help="Dos empresas con el mismo número (con y sin guiones): la integración no puede saber cuál es cuál.">
        <Table compact celled unstackable>
          <Table.Body>
            {r.companies.duplicatedCuit.flatMap((d) => d.companies.map((c) => (
              <Table.Row key={`${d.side}-${c._id}`}>
                <Table.Cell><b>{d.side === "local" ? local : peer}:</b> {c.name}</Table.Cell>
                <Table.Cell>{cuitCell(c, d.side, saveCompany)}</Table.Cell>
              </Table.Row>
            )))}
          </Table.Body>
        </Table>
      </ReviewSection>
      <ReviewSection title="Revisar: mismo nombre, CUIT distinto" color="yellow" count={r.companies.sameNameDifferentCuit.length}
        help="Si es la misma empresa, corregí el CUIT para que coincidan. Si son empresas distintas, está bien así.">
        <Table compact celled unstackable>
          <Table.Body>
            {r.companies.sameNameDifferentCuit.map((p) => (
              <Table.Row key={p.local._id}>
                <Table.Cell>{p.local.name}</Table.Cell>
                <Table.Cell><b>{local}:</b> {cuitCell(p.local, "local", saveCompany)}</Table.Cell>
                <Table.Cell><b>{peer}:</b> {p.peer.cuit || "(vacío)"}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </ReviewSection>
      <ReviewSection title="Se vinculan (mismo CUIT en las dos)" color="green" count={okMatches.length}>
        <Table compact celled unstackable>
          <Table.Body>
            {okMatches.map((m) => (
              <Table.Row key={m.local._id} warning={m.nameDiffers}>
                <Table.Cell>{m.local.cuit}</Table.Cell>
                <Table.Cell>{m.local.name}{m.nameDiffers && <> ≠ {m.peer.name} <small>(revisar si es la misma)</small></>}</Table.Cell>
                <Table.Cell>{m.linked ? <Label size="mini" color="green">Ya vinculada</Label> : ""}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </ReviewSection>
      <p className="integrations__review-help">Solo en {local}: {r.companies.onlyLocal.length} · Solo en {peer}: {r.companies.onlyPeer.length} (si se habilita el otro producto, se crean allá).</p>

      <Header as="h3">Sitios <small>(de las empresas que coinciden)</small></Header>
      <ReviewSection title="CUIT de relleno o mal cargado" color="red" count={r.sites.badCuit.length}>
        <Table compact celled unstackable>
          <Table.Body>
            {r.sites.badCuit.map((s) => (
              <Table.Row key={`${s.side}-${s._id}`}>
                <Table.Cell><b>{s.side === "local" ? local : peer}:</b> {s.empresa} / {s.name}</Table.Cell>
                <Table.Cell>{s.problem}</Table.Cell>
                <Table.Cell>{cuitCell(s, s.side, saveSite)}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </ReviewSection>
      <ReviewSection title={`Conflicto: el CUIT está en otra empresa en ${peer}`} color="red" count={r.sites.conflicts.length}
        help="Ese sitio no se va a poder replicar hasta que el CUIT sea el correcto.">
        <Table compact celled unstackable>
          <Table.Body>
            {r.sites.conflicts.map((s) => (
              <Table.Row key={s.local._id}>
                <Table.Cell>{s.empresa} / {s.local.name}</Table.Cell>
                <Table.Cell>{cuitCell(s.local, "local", saveSite)}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </ReviewSection>
      <ReviewSection title="Revisar: mismo nombre, CUIT distinto" color="yellow" count={r.sites.sameNameDifferentCuit.length}
        help="Si es el mismo sitio, corregí el CUIT. Si son sitios distintos que se llaman igual, está bien: se crea el que falta.">
        <Table compact celled unstackable>
          <Table.Body>
            {r.sites.sameNameDifferentCuit.map((s) => (
              <Table.Row key={s.local._id}>
                <Table.Cell>{s.empresa} / {s.local.name}</Table.Cell>
                <Table.Cell><b>{local}:</b> {cuitCell(s.local, "local", saveSite)}</Table.Cell>
                <Table.Cell><b>{peer}:</b> {s.peer.cuit}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </ReviewSection>
      <p className="integrations__review-help">
        Se vinculan (mismo CUIT): {r.sites.matched.length} · Se van a crear en {peer}: {r.sites.toCreateInPeer.length}
      </p>

      <Header as="h3">Usuarios con el mismo email en las dos apps</Header>
      <ReviewSection title={'Se pueden unir con "Vincular cuentas" (pestaña Empresas)'} color="green" count={r.users.linkable.length}>
        <Table compact celled unstackable>
          <Table.Body>
            {r.users.linkable.map((u) => (
              <Table.Row key={u.email}><Table.Cell>{u.email}</Table.Cell><Table.Cell>{local}: {u.localRole} · {peer}: {u.peerRole}</Table.Cell></Table.Row>
            ))}
          </Table.Body>
        </Table>
      </ReviewSection>
      <ReviewSection title="En empresas distintas o sin empresa (no se pueden vincular)" color="yellow" count={r.users.otherCompany.length}
        help={`Un usuario sin empresa que no sea master no puede entrar a ${local}.`}>
        <Table compact celled unstackable>
          <Table.Body>
            {r.users.otherCompany.map((u) => (
              <Table.Row key={u.email} warning={!u.localHasCompany || !u.peerHasCompany}>
                <Table.Cell>{u.email}</Table.Cell>
                <Table.Cell>{local}: {u.localRole}{!u.localHasCompany && " (sin empresa)"} · {peer}: {u.peerRole}{!u.peerHasCompany && " (sin empresa)"}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </ReviewSection>
      <p className="integrations__review-help">Master en las dos (no se vinculan, es normal): {r.users.masters.length} · Ya vinculadas: {r.users.alreadyLinked.length}</p>
    </div>
  );
}
